import React from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";

interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  labelledBy: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  noValidate?: boolean;
  maxWidthClassName?: string;
  disableClose?: boolean;
  contentRef?: React.RefObject<HTMLDivElement>;
  footerClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
  mobileSheet?: boolean;
  panelClassName?: string;
  overlayClassName?: string;
  headerMeta?: React.ReactNode;
  headerMetaClassName?: string;
  hideHeader?: boolean;
  variant?: "default" | "creation";
}

export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  labelledBy,
  children,
  footer,
  onSubmit,
  noValidate = false,
  maxWidthClassName = "max-w-[920px]",
  disableClose = false,
  contentRef,
  footerClassName,
  headerClassName,
  contentClassName,
  mobileSheet = false,
  panelClassName,
  overlayClassName,
  headerMeta,
  headerMetaClassName,
  hideHeader = false,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const animationFrame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const initialTarget =
        panel.querySelector<HTMLElement>("[autofocus]") ??
        panel.querySelector<HTMLElement>('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])') ??
        panel.querySelector<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ??
        panel;
      initialTarget.focus();
    });
    return () => {
      window.cancelAnimationFrame(animationFrame);
      const previousFocus = previousFocusRef.current;
      if (previousFocus?.isConnected) previousFocus.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const variantClass = (element: string) =>
    variant === "default" ? "" : `app-${variant}-modal-${element}`;

  const handleOverlayClick = () => {
    if (!disableClose) onClose();
  };

  const content = (
    <>
      {hideHeader ? (
        <h2 id={labelledBy} className="sr-only">
          {title}
        </h2>
      ) : (
        <div
          className={`sticky top-0 z-10 border-b border-[var(--app-border)] bg-[color:color-mix(in_srgb,var(--app-surface)_96%,transparent)] px-5 pb-3 backdrop-blur sm:px-8 ${mobileSheet ? "pt-[calc(env(safe-area-inset-top)+0.75rem)]" : "pt-5"} sm:pt-8 sm:pb-6 ${variantClass("header")} ${headerClassName ?? ""}`}
        >
          <div className="app-modal-header-row mb-2 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 id={labelledBy} className="app-modal-title mb-2 text-2xl font-semibold leading-none tracking-[-0.04em] text-[var(--app-text)] sm:text-3xl">
                {title}
              </h2>
              {subtitle ? <p className="app-modal-subtitle max-w-2xl text-sm leading-6 text-[var(--app-text-secondary)]">{subtitle}</p> : null}
              {headerMeta ? <div className={`mt-2 ${headerMetaClassName ?? ""}`}>{headerMeta}</div> : null}
            </div>
            <button
              onClick={onClose}
              disabled={disableClose}
              className="app-modal-close inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--ds-radius-sm)] text-[var(--app-muted-text)] transition-colors hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)] disabled:opacity-50"
              aria-label={t("common.prompts.closeModal")}
              type="button"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>
      )}

      {onSubmit ? (
        <form onSubmit={onSubmit} noValidate={noValidate} className="flex min-h-0 flex-1 flex-col">
          <div
            ref={contentRef}
            className={`min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-8 sm:pb-8 ${variantClass("content")} ${contentClassName ?? ""}`}
          >
            {children}
          </div>
          {footer ? (
            <div className={`sticky bottom-0 z-10 border-t border-[var(--app-border)] bg-[color:color-mix(in_srgb,var(--app-surface)_96%,transparent)] px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+var(--app-space-3))] backdrop-blur sm:px-8 sm:pt-6 sm:pb-6 ${variantClass("footer")} ${footerClassName ?? ""}`}>
              {footer}
            </div>
          ) : null}
        </form>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            ref={contentRef}
            className={`min-h-0 flex-1 overflow-y-auto px-5 pb-5 sm:px-8 sm:pb-8 ${variantClass("content")} ${contentClassName ?? ""}`}
          >
            {children}
          </div>
          {footer ? (
            <div className={`sticky bottom-0 z-10 border-t border-[var(--app-border)] bg-[color:color-mix(in_srgb,var(--app-surface)_96%,transparent)] px-5 pt-3 pb-[calc(env(safe-area-inset-bottom)+var(--app-space-3))] backdrop-blur sm:px-8 sm:pt-6 sm:pb-6 ${variantClass("footer")} ${footerClassName ?? ""}`}>
              {footer}
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex justify-center bg-[var(--app-overlay)] backdrop-blur-sm ${
          mobileSheet ? "items-end px-0 md:items-center md:px-4" : "items-center px-4"
      } ${variantClass("overlay")} ${overlayClassName ?? ""}`}
      onClick={handleOverlayClick}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            if (!disableClose) {
              event.preventDefault();
              onClose();
            }
            return;
          }
          if (event.key !== "Tab") return;
          const panel = panelRef.current;
          if (!panel) return;
          const focusable = Array.from(
            panel.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((element) => element.getClientRects().length > 0);
          if (focusable.length === 0) {
            event.preventDefault();
            panel.focus();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (document.activeElement === panel) {
            event.preventDefault();
            (event.shiftKey ? last : first)?.focus();
            return;
          }
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        ref={panelRef}
        className={`relative flex w-full flex-col overflow-hidden border-[var(--app-border)] bg-[var(--app-surface)] shadow-[var(--app-shadow-strong)] ${maxWidthClassName} ${
          mobileSheet
            ? "h-[100dvh] max-h-[100dvh] rounded-t-[var(--ds-radius-lg)] border-x-0 border-b-0 md:h-auto md:max-h-[90vh] md:rounded-[var(--ds-radius-xl)] md:border"
            : "max-h-[90vh] rounded-[var(--ds-radius-xl)] border"
        } ${variantClass("panel")} ${panelClassName ?? ""}`}
        onClick={(event) => event.stopPropagation()}
        tabIndex={-1}
      >
        {content}
      </div>
    </div>,
    document.body,
  );
};
